import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const INIT = { name: '', email: '', phone: '', company: '', source: 'other', status: 'new', assigned_to: '', notes: '' };
const LIMIT = 15;
const SOURCES = ['website', 'referral', 'social', 'email', 'phone', 'other'];
const STATUSES = ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'won', 'lost'];

const statusColor = {
  new: 'info', contacted: 'primary', qualified: 'warning',
  proposal: 'warning', negotiation: 'warning', won: 'active', lost: 'inactive',
};

export default function Leads({ setTitle }) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState(INIT);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [users, setUsers]       = useState([]);

  useEffect(() => { setTitle('Leads'); }, [setTitle]);
  useEffect(() => {
    api.get('/auth/users').then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ search, page, limit: LIMIT });
      if (filterStatus) params.set('status', filterStatus);
      const { data } = await api.get(`/leads?${params}`);
      setItems(data.data || []); setTotal(data.total || 0);
    } catch { setItems([]); } finally { setLoading(false); }
  }, [search, page, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(INIT); setEditId(null); setModal(true); };
  const openEdit = (item) => { setForm({ ...item, assigned_to: item.assigned_to || '' }); setEditId(item.id); setModal(true); };
  const closeModal = () => setModal(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) return alert('Name is required');
    setSaving(true);
    try {
      if (editId) await api.put(`/leads/${editId}`, form);
      else        await api.post('/leads', form);
      closeModal(); load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving lead');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete lead "${name}"?`)) return;
    try { await api.delete(`/leads/${id}`); load(); }
    catch (err) { alert(err.response?.data?.error || 'Error deleting'); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Leads</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Lead
        </button>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input placeholder="Search name, email, company…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <select className="form-control" style={{ width: 160 }} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} leads</span>
        </div>

        <div className="table-wrap">
          {loading ? <div className="loading">Loading…</div> : (
            <table>
              <thead>
                <tr>
                  <th>#</th><th>Name</th><th>Company</th><th>Email</th><th>Phone</th>
                  <th>Source</th><th>Status</th><th>Assigned To</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={9} className="empty">No leads found</td></tr>
                ) : items.map((item, i) => (
                  <tr key={item.id}>
                    <td className="text-muted">{(page - 1) * LIMIT + i + 1}</td>
                    <td><strong>{item.name}</strong></td>
                    <td>{item.company || '—'}</td>
                    <td className="text-muted">{item.email || '—'}</td>
                    <td>{item.phone || '—'}</td>
                    <td><span className="badge badge-info" style={{ textTransform: 'capitalize' }}>{item.source}</span></td>
                    <td><span className={`badge badge-${statusColor[item.status] || 'info'}`} style={{ textTransform: 'capitalize' }}>{item.status}</span></td>
                    <td>{item.assigned_name || '—'}</td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(item)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id, item.name)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
            <div className="pagination-btns">
              <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editId ? 'Edit Lead' : 'Add Lead'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full">
                  <label>Full Name *</label>
                  <input className="form-control" value={form.name} onChange={e => f('name', e.target.value)} placeholder="Lead name" />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" className="form-control" value={form.email} onChange={e => f('email', e.target.value)} placeholder="email@example.com" />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input className="form-control" value={form.phone} onChange={e => f('phone', e.target.value)} placeholder="+1 234 567 8900" />
                </div>
                <div className="form-group full">
                  <label>Company</label>
                  <input className="form-control" value={form.company} onChange={e => f('company', e.target.value)} placeholder="Company name" />
                </div>
                <div className="form-group">
                  <label>Source</label>
                  <select className="form-control" value={form.source} onChange={e => f('source', e.target.value)}>
                    {SOURCES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-control" value={form.status} onChange={e => f('status', e.target.value)}>
                    {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Assigned To</label>
                  <select className="form-control" value={form.assigned_to} onChange={e => f('assigned_to', e.target.value)}>
                    <option value="">— Unassigned —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="form-group full">
                  <label>Notes</label>
                  <textarea className="form-control" rows={3} value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Additional notes…" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update Lead' : 'Create Lead'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
